import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Smartphone } from 'lucide-react';

const chatSessions = [
  {
    id: 'emotional',
    messages: [
      { type: 'user', text: "I got rejected from a gallery I really cared about. I don't know if I'm good enough." },
      { type: 'ai', text: "I hear you — rejection stings, especially when it's personal work. But here's what the data says: your portfolio consistency is in the top 15% this quarter, and your Discipline score has climbed 40 points in 3 months. That gallery's loss. Let me find three others that match your style better." },
    ],
  },
  {
    id: 'financial',
    messages: [
      { type: 'user', text: "I haven't been paid for a commissioned piece. It's been 3 months. What can I do?" },
      { type: 'ai', text: "That's unacceptable. AINAA's Legal Aid panel handles exactly this. I'm flagging this for priority review — members with your Trust Score get fast-tracked. In the meantime, I'll draft a formal demand letter template you can send today. Want me to also check if this client has pending disputes with other members?" },
    ],
  },
  {
    id: 'career',
    messages: [
      { type: 'user', text: 'How do I get my work shown internationally? I feel stuck in my local scene.' },
      { type: 'ai', text: "You're actually closer than you think. Your Communication score is strong — 856 — which means curators find you reliable to work with. I see three open calls from European institutions this month that align with your medium. Your Trust Score also qualifies you for AINAA's International Showcase programme. Want me to start the application?" },
    ],
  },
  {
    id: 'growth',
    messages: [
      { type: 'user', text: 'My trust score dropped 12 points this month. What happened?' },
      { type: 'ai', text: "I looked into it — it's your Discipline dimension. You had 3 late submissions and missed a peer review deadline. The good news: these are fixable. Blocking your mornings for deep work has worked for members with similar patterns. Your Credibility and Communication are solid. One focused month and you'll recover those points." },
    ],
  },
  {
    id: 'personal',
    messages: [
      { type: 'user', text: "I'm burning out. I took on too many projects and I can't deliver on time." },
      { type: 'ai', text: "Your wellbeing matters more than any score. I can see your workload spiked 3x in the last 6 weeks — that's not sustainable. Let's triage: which 2 projects are most important to your career? I'll help you draft professional delay notices for the rest. AINAA members also have access to subsidised counselling through the Wellness Fund." },
    ],
  },
];

const SpecterSection = () => {
  const [activeChat, setActiveChat] = useState(0);

  const nextChat = useCallback(() => {
    setActiveChat(prev => (prev + 1) % chatSessions.length);
  }, []);

  useEffect(() => {
    const interval = setInterval(nextChat, 7000);
    return () => clearInterval(interval);
  }, [nextChat]);

  const current = chatSessions[activeChat];

  return (
    <section
      className="relative overflow-hidden py-20 sm:py-28 px-6 md:px-12"
      data-testid="specter-section"
      style={{ background: 'linear-gradient(160deg, #0A1220 0%, #0F1B2D 30%, #182B45 60%, #0F1B2D 100%)' }}
    >
      {/* Scan-line effect overlay */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.08) 2px, rgba(255,255,255,0.08) 4px)',
      }} />

      {/* Ambient glow */}
      <div className="absolute top-0 right-1/4 w-72 h-72 bg-cyan-500 rounded-full blur-[140px] opacity-[0.04]" />
      <div className="absolute bottom-0 left-1/3 w-60 h-60 bg-[#C4A35A] rounded-full blur-[120px] opacity-[0.05]" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          {/* Chat Demo */}
          <div className="order-2 lg:order-1">
            <div className="border border-white/10 bg-white/[0.03] backdrop-blur-sm p-6 lg:p-8 relative">
              {/* Header */}
              <div className="flex items-center gap-3 mb-5 pb-4 border-b border-white/10">
                <div className="w-9 h-9 bg-[#C4A35A]/10 border border-[#C4A35A]/30 flex items-center justify-center">
                  <MessageCircle size={16} className="text-[#C4A35A]" />
                </div>
                <div>
                  <p className="text-sm font-bold text-[#F5F3EE]">
                    Specter<sup className="text-[7px] text-[#C4A35A]">™</sup>
                  </p>
                  <p className="text-[9px] font-medium text-[#9B9589]">AI Assistant</p>
                </div>
                <div className="ml-auto flex items-center gap-1.5">
                  <div className="w-2 h-2 bg-emerald-400 animate-pulse" />
                  <span className="text-[9px] font-bold text-[#9B9589]">Active</span>
                </div>
              </div>

              {/* Rotating Messages */}
              <div className="h-[440px] sm:h-[280px] overflow-hidden relative">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={current.id}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    variants={{
                      enter: { opacity: 0 },
                      center: { opacity: 1, transition: { duration: 0.15, staggerChildren: 0.08 } },
                      exit: { opacity: 0, transition: { duration: 0.15, staggerChildren: 0.05 } },
                    }}
                    className="space-y-4 absolute inset-0"
                  >
                    {current.messages.map((msg) => (
                      <motion.div
                        key={msg.text.slice(0, 20)}
                        variants={{
                          enter: { x: msg.type === 'ai' ? -60 : 60, opacity: 0 },
                          center: { x: 0, opacity: 1, transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] } },
                          exit: { x: msg.type === 'ai' ? -60 : 60, opacity: 0, transition: { duration: 0.3 } },
                        }}
                        className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[88%] p-4 text-sm font-medium leading-relaxed ${
                            msg.type === 'user'
                              ? 'bg-white/5 border border-white/10 text-[#B0ACA4]'
                              : 'bg-[#C4A35A]/10 border border-[#C4A35A]/20 text-[#D4C9A8]'
                          }`}
                        >
                          {msg.text}
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Input */}
              <div className="mt-5 pt-4 border-t border-white/10">
                <div className="flex items-center gap-3 px-4 py-3 border border-white/10 bg-white/[0.02]">
                  <span className="text-sm font-medium text-[#9B9589]/50">Talk to Specter...</span>
                </div>
              </div>

              {/* Slide indicators */}
              <div className="flex items-center justify-center gap-2 mt-5">
                {chatSessions.map((session, i) => (
                  <button
                    key={session.id}
                    onClick={() => setActiveChat(i)}
                    className={`h-1 transition-all duration-300 ${
                      i === activeChat ? 'w-6 bg-[#C4A35A]' : 'w-2 bg-white/20'
                    }`}
                    data-testid={`specter-dot-${i}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="order-1 lg:order-2"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#C4A35A]/10 border border-[#C4A35A]/30 flex items-center justify-center">
                <MessageCircle size={18} className="text-[#C4A35A]" />
              </div>
              <div>
                <span className="text-[11px] uppercase tracking-[0.25em] text-[#F5F3EE] font-bold block">
                  Specter<sup className="text-[8px]">™</sup>
                </span>
                <span className="text-[9px] uppercase tracking-[0.15em] text-[#9B9589] font-medium">
                  Floating AI Assistant
                </span>
              </div>
            </div>

            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-[#F5F3EE] tracking-tight mb-10">
              Your best friend
              <span className="block text-[#C4A35A]/70 italic">in the industry.</span>
            </h2>

            <p className="text-[#B0ACA4] font-medium leading-relaxed mb-8 max-w-lg">
              Specter™ is a floating, context-aware AI assistant that lives inside the AINAA app. It knows your Trust Score, your goals, your discipline. Ask it anything — career advice, score improvement, financial help, or just talk. It adapts its tone to what you need: encouragement, strategy, or honest feedback.
            </p>

            <div className="space-y-3 mb-8">
              {[
                'Context-aware — knows your score, history, and goals',
                'Floating assistant — available anywhere in the app',
                'Adapts tone — supportive, strategic, or direct',
                'Connected to AINAA services — legal, financial, career',
              ].map((feature, index) => (
                <motion.div
                  key={feature}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-center gap-3"
                >
                  <div className="w-1.5 h-1.5 bg-[#C4A35A] flex-shrink-0" />
                  <span className="text-sm font-medium text-[#B0ACA4]">{feature}</span>
                </motion.div>
              ))}
            </div>

            <div className="border border-[#C4A35A]/30 bg-[#C4A35A]/5 p-3 inline-flex items-center gap-2">
              <Smartphone size={14} className="text-[#C4A35A]" />
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#C4A35A] font-bold">
                Available exclusively on AINAA Mobile App
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default SpecterSection;
